import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import { Stepper } from '../../../../../components/ui/stepper';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Brain,
  FileText,
  Activity,
  RefreshCw,
  ArrowRight,
  Shield,
  Eye,
  FileType,
  Scan,
  BookOpen,
  X,
  ChevronRight,
} from 'lucide-react';
import type { ClassificationResult, Step } from '../types';

/* ───────── Markdown-aware Document Renderer ───────── */

/**
 * Renders inline markdown: **bold**, *italic*, [links](url), `code`.
 * Strips <br> tags and image placeholders from pymupdf4llm output.
 */
const renderInline = (text: string): React.ReactNode => {
  // Split on **bold**, *italic*, and `code` patterns
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/s);
    if (boldMatch && boldMatch[2] && boldMatch[3] !== undefined) {
      if (boldMatch[1]) parts.push(boldMatch[1]);
      parts.push(<strong key={key++} className="font-semibold text-slate-900">{boldMatch[2]}</strong>);
      remaining = boldMatch[3];
      continue;
    }

    // No more patterns — push the rest
    parts.push(remaining);
    break;
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
};

/**
 * Renders pymupdf4llm markdown output as structured HTML.
 * Handles headings, bold, lists, numbered clauses, page markers,
 * and strips image placeholders.
 */
const DocumentRenderer: React.FC<{ text: string }> = ({ text }) => {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    let trimmed = (lines[i] || '').trim();

    // Strip <br> tags
    trimmed = trimmed.replace(/<br\s*\/?>/gi, ' ').trim();

    // Strip image/picture artifacts (whole-line or embedded inline)
    trimmed = trimmed.replace(/\*?\*?==>.*?omitted.*?<==\*?\*?/g, '').trim();
    trimmed = trimmed.replace(/\*?\*?-{3,}\s*(Start|End) of picture text\s*-{3,}\*?\*?/g, '').trim();

    // Skip if line became empty after stripping
    if (!trimmed) {
      elements.push(<div key={i} className="h-1.5" />);
      continue;
    }

    // Skip lone page numbers (e.g. "3", "17") — PDF page footer artifacts
    if (/^\d{1,3}$/.test(trimmed)) continue;

    // Page separator
    const pageMatch = trimmed.match(/^-- (\d+) of (\d+) --$/);
    if (pageMatch) {
      elements.push(
        <div key={i} className="flex items-center gap-2 my-6 select-none">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-[10px] font-medium text-slate-400 bg-slate-50 px-3 py-1 rounded-full">
            Page {pageMatch[1]} of {pageMatch[2]}
          </span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
      );
      continue;
    }

    // Empty line
    if (!trimmed) {
      elements.push(<div key={i} className="h-1.5" />);
      continue;
    }

    // H1 heading: # Title
    if (/^# (?!#)/.test(trimmed)) {
      elements.push(
        <h2 key={i} className="text-lg font-bold text-slate-900 mt-6 mb-2 pb-2 border-b border-slate-200">
          {renderInline(trimmed.slice(2))}
        </h2>
      );
      continue;
    }

    // H2 heading: ## Title
    if (/^## (?!#)/.test(trimmed)) {
      elements.push(
        <h3 key={i} className="text-base font-bold text-slate-800 mt-5 mb-1.5">
          {renderInline(trimmed.slice(3))}
        </h3>
      );
      continue;
    }

    // H3 heading: ### Title
    if (trimmed.startsWith('### ')) {
      elements.push(
        <h4 key={i} className="text-sm font-semibold text-slate-700 mt-4 mb-1">
          {renderInline(trimmed.slice(4))}
        </h4>
      );
      continue;
    }

    // Bold-only line = subheading (pymupdf4llm uses **bold** for subheadings)
    if (/^\*\*[^*]+\*\*$/.test(trimmed)) {
      const headingText = trimmed.replace(/^\*\*|\*\*$/g, '');
      elements.push(
        <h4 key={i} className="text-sm font-semibold text-slate-800 mt-4 mb-1 flex items-center gap-1.5">
          <div className="w-1 h-4 bg-blue-400 rounded-full shrink-0" />
          {headingText}
        </h4>
      );
      continue;
    }

    // Numbered sub-clause: 1.1, 2.3, etc.
    const clauseMatch = trimmed.match(/^(\d+\.\d+)\s+(.*)/);
    if (clauseMatch) {
      elements.push(
        <p key={i} className="text-sm text-slate-700 leading-relaxed pl-5 py-0.5 border-l-2 border-blue-200 my-1">
          <span className="font-semibold text-blue-700 mr-1.5">{clauseMatch[1]}</span>
          {renderInline(clauseMatch[2] || '')}
        </p>
      );
      continue;
    }

    // Top-level numbered item: 1. 2. 3. etc.
    if (/^\d+[\.\)]\s/.test(trimmed)) {
      elements.push(
        <p key={i} className="text-sm font-semibold text-slate-800 leading-relaxed mt-3 mb-1">
          {renderInline(trimmed)}
        </p>
      );
      continue;
    }

    // Bullet point
    if (/^[-•–—*]\s/.test(trimmed)) {
      elements.push(
        <div key={i} className="flex gap-2 pl-4 my-0.5">
          <span className="text-slate-400 shrink-0 mt-1">&#x2022;</span>
          <p className="text-sm text-slate-700 leading-relaxed">
            {renderInline(trimmed.replace(/^[-•–—*]\s+/, ''))}
          </p>
        </div>
      );
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={i} className="text-sm text-slate-700 leading-relaxed">
        {renderInline(trimmed)}
      </p>
    );
  }

  return <div className="max-w-none space-y-0">{elements}</div>;
};

/* ───────── Full Analysis Panel (portal overlay) ───────── */

interface AnalysisPanelProps {
  open: boolean;
  onClose: () => void;
  classificationResult: ClassificationResult;
}

const AnalysisPanel: React.FC<AnalysisPanelProps> = ({ open, onClose, classificationResult }) => {
  const { classification, extraction } = classificationResult;

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Panel — slides in from right, full height */}
      <div className="relative ml-auto w-full sm:max-w-3xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Panel Header — sticky */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Brain className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Document Analysis</h2>
              <p className="text-xs text-slate-500">Powered by Vastabloc &mdash; Yiontech Document Intelligence Engine</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-5 sm:space-y-6">

            {/* Classification Summary */}
            <section>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Classification</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-[10px] font-medium text-blue-600 uppercase tracking-wider mb-1">Document Type</p>
                  <p className="text-sm font-bold text-blue-900">{classification.documentType.replace(/_/g, ' ')}</p>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-[10px] font-medium text-green-600 uppercase tracking-wider mb-1">Confidence</p>
                  <p className="text-sm font-bold text-green-900">{Math.round(classification.confidence * 100)}%</p>
                </div>
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl">
                  <p className="text-[10px] font-medium text-purple-600 uppercase tracking-wider mb-1">Compliance</p>
                  <p className="text-sm font-bold text-purple-900">{classification.isComplianceRelated ? 'Yes' : 'No'}</p>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Action</p>
                  <p className="text-sm font-bold text-slate-900 capitalize">{classification.recommendedAction}</p>
                </div>
              </div>
            </section>

            {/* AI Reasoning */}
            {classification.reasoning && (
              <section>
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">AI Reasoning</h3>
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                    {classification.reasoning}
                  </p>
                </div>
              </section>
            )}

            {/* Document Metadata */}
            {extraction?.metadata && (
              <section>
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Document Metadata</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {extraction.metadata.name && (
                    <div className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                      <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Document Name</p>
                        <p className="text-sm font-semibold text-slate-800">{extraction.metadata.name}</p>
                      </div>
                    </div>
                  )}
                  {extraction.metadata.version && (
                    <div className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                      <Activity className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Version</p>
                        <p className="text-sm font-semibold text-slate-800">{extraction.metadata.version}</p>
                      </div>
                    </div>
                  )}
                  {extraction.metadata.effectiveDate && (
                    <div className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                      <BookOpen className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Effective Date</p>
                        <p className="text-sm font-semibold text-slate-800">{extraction.metadata.effectiveDate}</p>
                      </div>
                    </div>
                  )}
                  {extraction.metadata.detectedSource && (
                    <div className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                      <Scan className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Detected Source</p>
                        <p className="text-sm font-semibold text-slate-800">{extraction.metadata.detectedSource}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Technical Extraction Info */}
            {extraction && (extraction.detectedMimeType || extraction.textQuality || extraction.usedOCR || extraction.pageCount) && (
              <section>
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Extraction Details</h3>
                <div className="flex flex-wrap gap-2">
                  {extraction.detectedMimeType && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg border border-slate-200">
                      <FileType className="w-3.5 h-3.5" />
                      {extraction.detectedMimeType}
                    </span>
                  )}
                  {extraction.textQuality && (
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border ${
                      extraction.textQuality === 'good'
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-amber-50 text-amber-700 border-amber-200'
                    }`}>
                      <Scan className="w-3.5 h-3.5" />
                      Text quality: {extraction.textQuality}
                    </span>
                  )}
                  {extraction.usedOCR && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg border border-indigo-200">
                      <Eye className="w-3.5 h-3.5" />
                      OCR applied
                    </span>
                  )}
                  {extraction.pageCount && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg border border-slate-200">
                      <BookOpen className="w-3.5 h-3.5" />
                      {extraction.pageCount} pages
                    </span>
                  )}
                </div>
              </section>
            )}

            {/* Full Extracted Text — renders the entire document with structure */}
            {extraction?.previewText && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Full Extracted Document</h3>
                  <div className="flex items-center gap-3 text-[10px] text-slate-400">
                    <span>{extraction.previewText.length.toLocaleString()} characters</span>
                    {extraction.pageCount && <span>{extraction.pageCount} pages</span>}
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="bg-slate-50 border-b border-slate-200 px-5 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <FileText className="w-3.5 h-3.5" />
                      <span className="font-medium">Document Content</span>
                    </div>
                    <span className="text-[10px] text-slate-400">
                      Vastabloc extraction &middot; {extraction.textQuality === 'good' ? 'High quality' : 'Fair quality'}
                      {extraction.usedOCR ? ' &middot; OCR applied' : ''}
                    </span>
                  </div>
                  <div className="px-6 py-5">
                    <DocumentRenderer text={extraction.previewText} />
                  </div>
                </div>
              </section>
            )}

          </div>
        </div>

        {/* Panel Footer — sticky */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200 px-4 sm:px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Vastabloc encrypted pipeline</span>
            <span className="flex items-center gap-1"><Brain className="w-3 h-3" /> Yiontech Document Intelligence</span>
          </div>
          <Button onClick={onClose} variant="outline" size="sm">
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ───────── Main Component ───────── */

interface ClassificationResultPhaseProps {
  classificationResult: ClassificationResult;
  steps: Step[];
  currentStep: number;
  onReset: () => void;
  onProceed: () => void;
}

export const ClassificationResultPhase: React.FC<ClassificationResultPhaseProps> = ({
  classificationResult,
  steps,
  currentStep,
  onReset,
  onProceed,
}) => {
  const { classification, message, extraction } = classificationResult;
  const isWarning = classification.recommendedAction === 'warn';
  const isRejected = classification.recommendedAction === 'reject';
  const [panelOpen, setPanelOpen] = useState(false);

  const confidencePercent = Math.round(classification.confidence * 100);

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 max-w-[1400px] mx-auto">
      {/* Compact Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-xl ${
              isRejected
                ? 'bg-red-500'
                : isWarning
                ? 'bg-amber-500'
                : 'bg-green-500'
            }`}
          >
            {isRejected ? (
              <XCircle className="w-5 h-5 text-white" />
            ) : isWarning ? (
              <AlertTriangle className="w-5 h-5 text-white" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-white" />
            )}
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{message.title}</h1>
            <p className="text-xs text-slate-500">{message.message}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={confidencePercent > 80 ? 'success' : 'warning'} className="font-bold">
            {confidencePercent}% Confidence
          </Badge>
          <Badge variant="info">
            {classification.documentType.replace(/_/g, ' ').toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Stepper */}
      <Card className="border border-gray-200">
        <CardContent className="p-3">
          <Stepper steps={steps} currentStep={currentStep} size="sm" showSpinner={false} />
        </CardContent>
      </Card>

      {/* Classification Summary — compact stat row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
        <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-blue-200 bg-blue-50">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-medium text-blue-600 uppercase tracking-wider">Type</p>
            <p className="text-xs font-semibold text-blue-900 truncate">{classification.documentType.replace(/_/g, ' ')}</p>
          </div>
        </div>
        <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-green-200 bg-green-50">
          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-medium text-green-600 uppercase tracking-wider">Compliance</p>
            <p className="text-xs font-semibold text-green-900">{classification.isComplianceRelated ? 'Yes' : 'No'}</p>
          </div>
        </div>
        <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-purple-200 bg-purple-50">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
            <Activity className="w-4 h-4 text-purple-600" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-medium text-purple-600 uppercase tracking-wider">Action</p>
            <p className="text-xs font-semibold text-purple-900 capitalize">{classification.recommendedAction}</p>
          </div>
        </div>
        {extraction?.pageCount && (
          <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50">
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <BookOpen className="w-4 h-4 text-slate-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Pages</p>
              <p className="text-xs font-semibold text-slate-900">{extraction.pageCount}</p>
            </div>
          </div>
        )}
      </div>

      {/* AI Reasoning — inline summary + link to open full panel */}
      {classification.reasoning && (
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <Brain className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 mb-1">AI Analysis</p>
                <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                  {classification.reasoning}
                </p>
              </div>
              <button
                onClick={() => setPanelOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors shrink-0 mt-0.5"
              >
                Read full analysis
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <Button onClick={onReset} variant="outline" className="flex-1">
          <RefreshCw className="w-4 h-4 mr-2" />
          {isRejected ? 'Upload Different File' : 'Cancel & Upload Different'}
        </Button>

        {(message.canProceed || isWarning) && (
          <Button
            onClick={onProceed}
            className={`flex-1 ${
              isWarning
                ? 'bg-amber-600'
                : 'bg-blue-600 hover:bg-blue-700'
            } shadow-md`}
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            {isWarning ? 'Continue Anyway' : 'Continue Processing'}
          </Button>
        )}
      </div>

      {/* Footer info */}
      <div className="flex items-center justify-center gap-5 text-[11px] text-slate-400">
        <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> End-to-end encrypted</span>
        <span className="flex items-center gap-1"><Brain className="w-3 h-3" /> Vastabloc Document Intelligence</span>
      </div>

      {/* Full Analysis Slide-over Panel */}
      <AnalysisPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        classificationResult={classificationResult}
      />
    </div>
  );
};
